import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, Upload, Loader2, AlertTriangle, Clock } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import api, { formatPrice } from "@/lib/api";
import { extractReceiptUrl, sha256File } from "@/lib/kaspiQr";

const KASPI_URL = "https://pay.kaspi.kz/pay/5rxvuomq";

export default function KaspiPaymentStep({ payment }) {
  const { t } = useLanguage();
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | scanning | uploading
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const prepay = payment?.requestedAmountKzt || 5000;

  const rejectMessage = (reason) => {
    if (reason === "underpaid") return t("booking.rejectedUnderpaid");
    if (reason === "receipt_already_used") return t("booking.rejectedUsed");
    if (reason === "seller_mismatch" || reason === "invalid_receipt_url") return t("booking.rejectedSeller");
    return t("booking.rejectedOther");
  };

  const handleVerify = async () => {
    if (!file) return;
    setError("");
    setResult(null);
    if (file.size > 10 * 1024 * 1024) {
      setError(t("booking.fileTooBig"));
      return;
    }
    try {
      setPhase("scanning");
      const [receiptUrl, sha] = await Promise.all([extractReceiptUrl(file), sha256File(file)]);
      setPhase("uploading");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("receipt_url", receiptUrl || "");
      fd.append("file_sha256", sha);
      const r = await api.post(`/payments/${payment.id}/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("booking.error"));
    } finally {
      setPhase("idle");
    }
  };

  const busy = phase !== "idle";
  const status = result?.status;

  if (status === "confirmed") {
    return (
      <div className="bg-card border border-primary/40 p-6 text-center space-y-3" data-testid="payment-confirmed">
        <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-primary" />
        </div>
        <h3 className="font-serif text-2xl">{t("booking.confirmedTitle")}</h3>
        <p className="text-sm text-muted-foreground">{t("booking.confirmedDesc")}</p>
        {result?.actualAmountKzt ? (
          <p className="text-sm">{t("booking.paidAmount")}: <span className="text-primary font-semibold">{formatPrice(result.actualAmountKzt)}</span></p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kaspi-payment-step">
      <div className="bg-card border border-border p-6 space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-muted-foreground">{t("booking.prepayLabel")}</span>
          <span className="text-2xl font-serif text-primary" data-testid="prepay-amount">{formatPrice(prepay)}</span>
        </div>
        <p className="text-sm text-foreground leading-relaxed">{t("booking.successDesc")}</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{t("booking.successReceipt")}</p>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="no-refund-note">
          {t("booking.noRefund")}
        </p>
      </div>

      <a href={KASPI_URL} target="_blank" rel="noreferrer" className="block">
        <Button className="w-full bg-[#F14635] hover:bg-[#d83c2c] text-white px-8 py-6 rounded-none text-base gap-3" data-testid="booking-kaspi-pay-btn">
          <CreditCard className="w-5 h-5" /> {t("booking.payBtn")}
        </Button>
      </a>

      <div className="bg-card border border-border p-6 space-y-4">
        <h3 className="font-serif text-xl">{t("booking.uploadTitle")}</h3>
        <label className="flex items-center gap-3 border border-dashed border-border px-4 py-4 cursor-pointer hover:border-primary/40 transition-colors">
          <Upload className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{file ? file.name : t("booking.chooseFile")}</p>
            <p className="text-xs text-muted-foreground">{t("booking.uploadHint")}</p>
          </div>
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(""); }}
            data-testid="receipt-file-input"
          />
        </label>

        <Button
          onClick={handleVerify}
          disabled={!file || busy}
          className="w-full bg-primary text-primary-foreground rounded-none py-6 gap-2 hover:bg-primary/90"
          data-testid="verify-receipt-btn"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {phase === "scanning" ? t("booking.qrSearching") : phase === "uploading" ? t("booking.verifying") : t("booking.verifyBtn")}
        </Button>

        {error && (
          <p className="text-sm text-destructive flex items-start gap-2" data-testid="payment-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </p>
        )}

        {status === "manual_review" && (
          <div className="border border-amber-300 bg-amber-50 p-4 space-y-1" data-testid="payment-manual-review">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <Clock className="w-4 h-4" /> {t("booking.manualTitle")}
            </p>
            <p className="text-xs text-amber-800">{t("booking.manualDesc")}</p>
          </div>
        )}

        {status === "rejected" && (
          <div className="border border-destructive/40 bg-destructive/5 p-4" data-testid="payment-rejected">
            <p className="text-sm text-destructive">{rejectMessage(result?.reviewReason)}</p>
            {result?.actualAmountKzt ? (
              <p className="text-xs text-muted-foreground mt-1">
                {t("booking.paidAmount")}: {formatPrice(result.actualAmountKzt)} / {formatPrice(prepay)}
              </p>
            ) : null}
          </div>
        )}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        <a href="https://wa.me/77027033030" target="_blank" rel="noreferrer" className="underline hover:text-primary" data-testid="booking-whatsapp-link">
          {t("booking.whatsappContact")}
        </a>
      </p>
    </div>
  );
}
